/**
 * @license
 * Copyright 2012 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * @fileoverview Generating JavaScript for loop blocks.
 * @author fraser@google.com (Neil Fraser)
 */
'use strict';

import {invertCondition} from './logic';

/*
goog.provide('Blockly.BBasic.loops');

goog.require('Blockly.BBasic');
*/


// A "repeat N times" block's  count, when N is a complex expression (not
// a plain number or bare variable name) - see controls_repeat_ext's
// comment below for why this needs a real, dedicated, properly-declared
// variable rather than a shared scratch register like temp1 (which the
// loop's  body can just as easily be using for something else entirely,
// e.g. background_change_pixel, and "for X = 1 to <bound>" re-reads its
// bound from memory every iteration rather than caching it once). Reserved
// via generators/bbasic.js's  init() pre-scan/reserveDevVar bucket - the
// same mechanism every other feature's  hidden state uses (see e.g.
// reserveRomNoiseDevVars in generators/bbasic/sprites.js) - NOT
// nameDB_.getDistinctName, which (confirmed directly, see bbasic.js's
// comment on why temp1-6 had to be reserved against user variable name
// collisions) never actually gets a matching "dim" declared anywhere; a
// symbol nothing declares. One shared var project-wide - same reasoning
// REPEAT_COUNTER_VAR_NAME itself already gets away with sharing across every
// repeat loop: nested repeat blocks (a repeat whose body contains
// ANOTHER repeat block with a complex count) aren't safe with a single
// shared var either way, an existing limitation this doesn't make any worse.
export const REPEAT_BOUND_VAR_NAME = 'repeatBound';

// The "repeat X times" block's "for X = 1 to <bound> : ... : next" loop
// variable itself - used to be the literal, hardcoded bB identifier
// "loopcounter", unconditionally reserved a whole letter (or Superchip var0-
// 14 slot) in generators/bbasic.js's  SYSTEM_VARIABLES regardless of
// whether the project has any "Repeat" block at all. Resolved through
// nameDB_/reserveDevVar instead now, the same as REPEAT_BOUND_VAR_NAME right
// above (reserved only when generators/bbasic.js's  repeatLoopUsed
// pre-scan finds a repeat block) - a project with zero "Repeat" blocks no
// longer pays for this at all. One shared var project-wide, same reasoning
// as REPEAT_BOUND_VAR_NAME's  comment.
export const REPEAT_COUNTER_VAR_NAME = 'repeatcounter';

// wait_frames' "for X = 1 to <frames>" loop counter - deliberately NOT
// the shared REPEAT_COUNTER_VAR_NAME variable controls_repeat_ext's
// "for" loop uses, even though that's what this block itself used to share.
// A "Wait N frames" block placed inside a "Repeat X times" block's body is a
// real, reported case - both blocks' "for <counter> = 1 to ... next"
// constructs would fight over the exact same variable, with the INNER
// (wait_frames) loop's  final value clobbering the OUTER (repeat) loop's
// still-in-progress count the moment the wait finishes, corrupting however
// many iterations the repeat had left. Reserved the same way
// REPEAT_BOUND_VAR_NAME is (see its  comment just above) - a real,
// properly-declared dev var, not nameDB_.getDistinctName.
export const WAIT_FRAMES_COUNTER_VAR_NAME = 'waitFramesCounter';

// Whether a given "repeat N times" block's  TIMES input would actually
// need REPEAT_BOUND_VAR_NAME once controls_repeat_ext's  generator runs
// (see its "endVar" logic below) - used by generators/bbasic.js's
// early pre-scan (before reserveDevVar hands out letters) to reserve
// REPEAT_BOUND_VAR_NAME only for a project where at least one repeat block's
// count genuinely needs it, rather than for every project with ANY
// repeat block at all (the previous, simpler-but-wasteful gate - see that
// pre-scan's  comment for the one-byte tradeoff this replaces).
// Deliberately conservative: anything this can't positively PROVE simple
// (a bare math_number literal, or a bare variable getter) is treated as
// needing the var, same as the real generator would for anything else -
// this can only ever reserve one byte MORE than strictly necessary, never
// fewer, so it can't introduce the "reserveDevVar skipped, but generator
// referenced it anyway" class of bug a false negative here would cause.
export const repeatBoundVarNeeded = (block, Blockly) => {
  // controls_repeat (the older block, not in this app's  toolbox but
  // still valid in an existing saved project) uses a plain inline NUMBER
  // FIELD for TIMES, not a value input to plug expressions into - see the
  // generator's "if (block.getField('TIMES'))" branch, which always
  // produces a clean decimal string from it, never anything complex.
  if (block.getField('TIMES')) return false;
  const target = block.getInputTargetBlock('TIMES');
  if (!target) return true;
  if (target.type === 'variables_get') return false;
  if (target.type !== 'math_number') return true;
  // Mirrors math_number's  generator (generators/bbasic/math.js) exactly -
  // has to reproduce that same hex/binary/decimal parsing here rather than
  // just checking the raw field text, since a hex ("$1F") or binary
  // ("%1010") literal produces a CODE string ("$1F"/"%1010") that neither
  // matches /^\w+$/ nor Blockly.isNumber() - the exact same "needs the bound
  // var" case a genuinely complex expression hits, even though the block
  // itself is a plain literal.
  const raw = String(target.getFieldValue('NUM')).trim();
  let code;
  if (/^\$[0-9a-fA-F]+$/.test(raw)) code = raw;
  else if (/^0[xX][0-9a-fA-F]+$/.test(raw)) code = '$' + raw.slice(2);
  else if (/^%[01]+$/.test(raw)) code = raw;
  else if (/^0[bB][01]+$/.test(raw)) code = '%' + raw.slice(2);
  else code = String(Number(raw));
  return !(/^\w+$/.test(code)) && !Blockly.isNumber(code);
};

export default (Blockly) => {
  Blockly.BBasic['controls_repeat_ext'] = function(block) {
  // Repeat n times.
    let repeats;
    if (block.getField('TIMES')) {
    // Internal number.
      repeats = String(Number(block.getFieldValue('TIMES')));
    } else {
    // External number.
      repeats = Blockly.BBasic.valueToCode(block, 'TIMES',
          Blockly.BBasic.ORDER_ASSIGNMENT) || '0';
    }
    let branch = Blockly.BBasic.statementToCode(block, 'DO');
    branch = Blockly.BBasic.addLoopTrap(branch, block);
    let code = '';
    // "for X = 1 to <bound>" is a whitespace-sensitive positional
    // construct, same as "pfpixel X Y OPERATION" (see
    // background_change_pixel's  comment in generators/bbasic/
    // background.js) and wait_frames'  identical fix - a multi-token
    // bound (e.g. a Random block's "(rand / 4) + 1", which has spaces
    // in it) breaks it, confirmed directly as a real build failure. A
    // plain number or bare variable name is already safe to use directly;
    // anything else needs pre-assigning to REPEAT_BOUND_VAR_NAME first
    // (see its  comment above for why that, not temp1).
    let endVar = repeats;
    if (!repeats.match(/^\w+$/) && !Blockly.isNumber(repeats)) {
      endVar = Blockly.BBasic.nameDB_.getName(REPEAT_BOUND_VAR_NAME, Blockly.Names.DEVELOPER_VARIABLE_TYPE);
      code += endVar + ' = ' + repeats + '\n';
    }

    if (!branch.trim()) {
      branch = 'a = a\n';
    }

    // Kept as real, separate lines (never colon-joined onto one physical
    // line, as an earlier version of this did) - a loop body containing
    // ANY block that emits its  label (controls_if's "@ _if_N_bodyN"
    // goto targets, chief among them, but also score_set, background fades,
    // Text Minikernel blocks, etc.) breaks under colon-joining: a label has
    // to sit at the START of its  line, and squashing it onto the same
    // line as neighboring statements via " : " produces a stray "@"
    // mid-statement - a real, reported compile error ("unrecognized
    // character '@'") from an "if" block nested inside a "repeat" block.
    // Plain multi-line "for X = 1 to Y" / body / "next" is standard,
    // already-proven-working batari Basic syntax (same shape
    // Blockly.BBasic.normalizeIndents() already handles correctly for every
    // OTHER block's  multi-line, label-bearing output), so there was
    // never a real need to flatten this to one line in the first place.
    if (!branch.endsWith('\n')) branch += '\n';
    const counter = Blockly.BBasic.nameDB_.getName(REPEAT_COUNTER_VAR_NAME, Blockly.Names.DEVELOPER_VARIABLE_TYPE);
    code += 'for ' + counter + ' = 1 to ' + endVar + '\n' +
      branch +
      'next\n';

    return code;
  };

  Blockly.BBasic['controls_repeat'] =
    Blockly.BBasic['controls_repeat_ext'];

  Blockly.BBasic['wait_frames'] = function(block) {
  // Wait a number of video frames. Each frame runs the same per-frame work as
  // the main loop (gosub commongamelogic : drawscreen) so sprite colors, sizes,
  // animations and sound keep updating and the screen keeps drawing normally
  // during the wait, instead of freezing or losing the sprites. commongamelogic
  // is fixed, always-bank-1 content (see bbasic.bb.hbs) - needs its  bank
  // tag whenever this block itself is relocated away from bank 1 (see
  // bankJumpSuffix), same as generateGameLoopEvent's  identical fix.
    const argument0 = Blockly.BBasic.valueToCode(block, 'FRAMES',
        Blockly.BBasic.ORDER_ASSIGNMENT) || '1';
    const suffix = Blockly.BBasic.bankJumpSuffix(Blockly.BBasic.getCurrentBank(), Blockly.BBasic.primaryBank());
    const counter = Blockly.BBasic.nameDB_.getName(
        WAIT_FRAMES_COUNTER_VAR_NAME, Blockly.Names.DEVELOPER_VARIABLE_TYPE);
    // NOT a "for X = 1 to <bound>" loop (what this used to be): that
    // construct re-reads its  bound from memory every iteration rather
    // than caching it once (see REPEAT_BOUND_VAR_NAME's  comment above),
    // and this loop's  BODY unconditionally runs "gosub commongamelogic"
    // every iteration, which itself always calls "gosub
    // _run_once_edge_reset" first thing, whose hand-written asm
    // unconditionally does "STA temp1" - clobbering a temp1-held bound with
    // unrelated "Run once" bookkeeping bits after the very first iteration,
    // regardless of where the "Wait N frames" block itself is placed (a
    // Function, a Subroutine, or plain top-level code - commongamelogic
    // runs the exact same way every time either way). Confirmed as a real
    // reported bug this way: a "Wait N frames" block waited far longer than
    // the number actually entered, every single time.
    //
    // Rewritten as a plain count-DOWN-to-zero loop instead, using only
    // this block's  already-reserved counter (see
    // WAIT_FRAMES_COUNTER_VAR_NAME's  comment) as both the starting
    // count AND the live remaining-count - nothing else in the compiler
    // ever touches this dedicated dev var, so there's no second "bound"
    // variable to reserve or clobber: each iteration just decrements it and
    // loops again until it hits 0, sidestepping the "for...to" bound-reread
    // problem entirely rather than working around it.
    const labelBase = `_wait_frames_${Blockly.BBasic.blockNumbers.next('waitFrames')}`;
    return `${counter} = ${argument0}\n` +
      `@${labelBase}\n` +
      `gosub commongamelogic${suffix} : drawscreen\n` +
      `${counter} = ${counter} - 1\n` +
      `if ${counter} <> 0 then goto ${labelBase}\n`;
  };

  Blockly.BBasic['controls_whileUntil'] = function(block) {
  // Do while/until loop.
    const blockNumber = Blockly.BBasic.blockNumbers.next();
    const isUntil = block.getFieldValue('MODE') == 'UNTIL';

    const labelType = isUntil ? 'until' : 'while';
    const labelName = `_${labelType}_${blockNumber}_`;
    const startLabelName = labelName + 'start';
    const loopLabelName = labelName + 'loop';
    const endLabelName = labelName + 'end';
    const argument0 = Blockly.BBasic.valueToCode(block, 'BOOL', Blockly.BBasic.ORDER_NONE) || 'false';
    let branch = Blockly.BBasic.statementToCode(block, 'DO');
    branch = Blockly.BBasic.addLoopTrap(branch, block); // eslint-disable-line

    // "while" leaves when the opposite condition holds - one branch and one jump
    // instead of a branch and three jumps - when that opposite can be written.
    const opposite = isUntil ? null : invertCondition(argument0.trim());
    const codeForCondition = isUntil ?
      `if ${argument0} then goto ${endLabelName}` :
      opposite !== null ?
        `if ${opposite} then goto ${endLabelName}` :
        `if ${argument0} then goto ${loopLabelName} else goto ${endLabelName}`;

    return [
      '@' + startLabelName,
      codeForCondition,
      '@' + loopLabelName,
      branch,
      'goto ' + startLabelName,
      '@' + endLabelName,
    ].join('\n') + '\n';
  };

  Blockly.BBasic['controls_for'] = function(block) {
  // For loop.
    const variable0 = Blockly.BBasic.nameDB_.getName(
        block.getFieldValue('VAR'), Blockly.VARIABLE_CATEGORY_NAME);
    const argument0 = Blockly.BBasic.valueToCode(block, 'FROM',
        Blockly.BBasic.ORDER_ASSIGNMENT) || '0';
    const argument1 = Blockly.BBasic.valueToCode(block, 'TO',
        Blockly.BBasic.ORDER_ASSIGNMENT) || '0';
    const increment = Blockly.BBasic.valueToCode(block, 'BY',
        Blockly.BBasic.ORDER_ASSIGNMENT) || '1';
    let branch = Blockly.BBasic.statementToCode(block, 'DO');
    branch = Blockly.BBasic.addLoopTrap(branch, block);
    let code;
    if (Blockly.isNumber(argument0) && Blockly.isNumber(argument1) &&
      Blockly.isNumber(increment)) {
    // All arguments are simple numbers.
      const up = Number(argument0) <= Number(argument1);
      code = 'for (' + variable0 + ' = ' + argument0 + '; ' +
        variable0 + (up ? ' <= ' : ' >= ') + argument1 + '; ' +
        variable0;
      const step = Math.abs(Number(increment));
      if (step == 1) {
        code += up ? '++' : '--';
      } else {
        code += (up ? ' += ' : ' -= ') + step;
      }
      code += ') {\n' + branch + '}\n';
    } else {
      code = '';
      // Cache non-trivial values to variables to prevent repeated look-ups.
      let startVar = argument0;
      if (!argument0.match(/^\w+$/) && !Blockly.isNumber(argument0)) {
        startVar = Blockly.BBasic.nameDB_.getDistinctName(
            variable0 + '_start', Blockly.VARIABLE_CATEGORY_NAME);
        code += 'var ' + startVar + ' = ' + argument0 + ';\n';
      }
      let endVar = argument1;
      if (!argument1.match(/^\w+$/) && !Blockly.isNumber(argument1)) {
        endVar = Blockly.BBasic.nameDB_.getDistinctName(
            variable0 + '_end', Blockly.VARIABLE_CATEGORY_NAME);
        code += 'var ' + endVar + ' = ' + argument1 + ';\n';
      }
      // Determine loop direction at start, in case one of the bounds
      // changes during loop execution.
      const incVar = Blockly.BBasic.nameDB_.getDistinctName(
          variable0 + '_inc', Blockly.VARIABLE_CATEGORY_NAME);
      code += 'var ' + incVar + ' = ';
      if (Blockly.isNumber(increment)) {
        code += Math.abs(increment) + ';\n';
      } else {
        code += 'Math.abs(' + increment + ');\n';
      }
      code += 'if (' + startVar + ' > ' + endVar + ') {\n';
      code += Blockly.BBasic.INDENT + incVar + ' = -' + incVar + ';\n';
      code += '}\n';
      code += 'for (' + variable0 + ' = ' + startVar + '; ' +
        incVar + ' >= 0 ? ' +
        variable0 + ' <= ' + endVar + ' : ' +
        variable0 + ' >= ' + endVar + '; ' +
        variable0 + ' += ' + incVar + ') {\n' +
        branch + '}\n';
    }
    return code;
  };

  Blockly.BBasic['controls_forEach'] = function(block) {
  // For each loop.
    const variable0 = Blockly.BBasic.nameDB_.getName(
        block.getFieldValue('VAR'), Blockly.VARIABLE_CATEGORY_NAME);
    const argument0 = Blockly.BBasic.valueToCode(block, 'LIST',
        Blockly.BBasic.ORDER_ASSIGNMENT) || '[]';
    let branch = Blockly.BBasic.statementToCode(block, 'DO');
    branch = Blockly.BBasic.addLoopTrap(branch, block);
    let code = '';
    // Cache non-trivial values to variables to prevent repeated look-ups.
    let listVar = argument0;
    if (!argument0.match(/^\w+$/)) {
      listVar = Blockly.BBasic.nameDB_.getDistinctName(
          variable0 + '_list', Blockly.VARIABLE_CATEGORY_NAME);
      code += 'var ' + listVar + ' = ' + argument0 + ';\n';
    }
    const indexVar = Blockly.BBasic.nameDB_.getDistinctName(
        variable0 + '_index', Blockly.VARIABLE_CATEGORY_NAME);
    branch = Blockly.BBasic.INDENT + variable0 + ' = ' +
      listVar + '[' + indexVar + '];\n' + branch;
    code += 'for (var ' + indexVar + ' in ' + listVar + ') {\n' + branch + '}\n';
    return code;
  };

  Blockly.BBasic['controls_flow_statements'] = function(block) {
  // Flow statements: continue, break.
    let xfix = '';
    if (Blockly.BBasic.STATEMENT_PREFIX) {
    // Automatic prefix insertion is switched off for this block.  Add manually.
      xfix += Blockly.BBasic.injectId(Blockly.BBasic.STATEMENT_PREFIX,
          block);
    }
    if (Blockly.BBasic.STATEMENT_SUFFIX) {
    // Inject any statement suffix here since the regular one at the end
    // will not get executed if the break/continue is triggered.
      xfix += Blockly.BBasic.injectId(Blockly.BBasic.STATEMENT_SUFFIX,
          block);
    }
    if (Blockly.BBasic.STATEMENT_PREFIX) {
      // Blockly 8 made CONTROL_FLOW_IN_LOOP_CHECK_MIXIN (this used to read
      // getSurroundLoop off of) a local, unexported closure const in
      // node_modules/blockly/blocks/loops.js - Blockly.Constants.Loops no
      // longer exists at all. getSurroundLoop is still reachable the way
      // it's actually meant to be used, though: the stock
      // controls_flow_statements block's JSON definition lists
      // 'controls_flow_in_loop_check' in its extensions, which mixes
      // getSurroundLoop directly onto every real instance of this block -
      // block.getSurroundLoop() (no arguments; it reads `this` internally)
      // is the same method, just called the way Blockly actually calls
      // it internally, not through a now-gone static namespace path.
      const loop = block.getSurroundLoop();
      if (loop && !loop.suppressPrefixSuffix) {
      // Inject loop's statement prefix here since the regular one at the end
      // of the loop will not get executed if 'continue' is triggered.
      // In the case of 'break', a prefix is needed due to the loop's suffix.
        xfix += Blockly.BBasic.injectId(Blockly.BBasic.STATEMENT_PREFIX,
            loop);
      }
    }
    switch (block.getFieldValue('FLOW')) {
      case 'BREAK':
        return xfix + 'break;\n';
      case 'CONTINUE':
        return xfix + 'continue;\n';
    }
    throw Error('Unknown flow statement.');
  };
};
